import Fuse from "fuse.js";
import { isEmpty } from "ramda";
import { useEffect, useMemo } from "react";
import { Icon, IconButton, Typography, SidePanel, Skeleton } from "@/components/atoms";
import { useAppSelector } from "@/redux/hooks";
import { bookingHistoryLogsSelector } from "@/redux/slices/booking/selectors";
import { useSelectedBooking } from "../../hooks/useSelectedBooking";
import { JobHistorySearchFilters } from "./JobHistorySearchFilters";
import { JobHistoryTable } from "./JobHistoryTable";
import { FILTERS } from "./fixtures";
import { useInfiniteJobHistoryLogs } from "./useInfiniteJobHistoryLogs";
import { useJobHistorySearchFilters } from "./useJobHistorySearchFilters";

interface JobHistoryProps {
  open: boolean;
  onClose: () => void;
}

export const JobHistory = ({ open, onClose }: JobHistoryProps) => {
  const { id: bookingId, jobNumber } = useSelectedBooking();
  const searchFiltersContext = useJobHistorySearchFilters();
  const { mode, search, setSearch, filters, setFilters } = searchFiltersContext;
  const { isFetching: isLogsLoading, ref, getBookingHistoryLogs } = useInfiniteJobHistoryLogs();
  const logs = useAppSelector(bookingHistoryLogsSelector);

  const fuse = useMemo(
    () =>
      new Fuse(logs ?? [], {
        keys: ["modifier", "field.name", "field.from", "field.to", "event.name", "event.type"],
      }),
    [logs]
  );

  const data = useMemo(() => {
    if (search) return fuse.search(search).map((i) => i.item);

    // based on filters, return logs where log.event.type is in filters
    if (!isEmpty(filters)) {
      return (
        logs?.filter((i) => {
          if (i.event.type === "notification" && (i.event.name === "sms" || i.event.name === "voice")) return filters.includes("smsCall");
          else if (i.event.type === "notification" && i.event.name === "email") return filters.includes("email");
          else return filters.includes(i.event.type);
        }) ?? []
      );
    }

    return logs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, fuse, logs, mode, search]);

  useEffect(() => {
    if (open) getBookingHistoryLogs({ id: bookingId, page: "1" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setSearch("");
    setFilters(Object.keys(FILTERS));
    onClose();
  };

  return (
    <SidePanel open={open} onClose={handleClose} width={800} focused ref={ref}>
      <section className="flex items-center justify-between p-5">
        <div className="flex items-center gap-2">
          <Icon name="CalendarCircle" size="lg" />
          <Typography variant="title">Job History ({jobNumber})</Typography>
        </div>
        <div className="ml-3 flex h-7 items-center">
          <IconButton iconName="ArrowRight" onClick={handleClose} className="-mr-2" variant="custom" />
        </div>
      </section>
      <section className="flex flex-col p-5">
        <JobHistorySearchFilters searchFiltersContext={searchFiltersContext} />
        {!data ? <Skeleton /> : <JobHistoryTable logs={data} />}
        {isLogsLoading && <Skeleton variant="short" />}
      </section>
    </SidePanel>
  );
};
